import { OneTimeSecretDisplay } from '@/components/common/OneTimeSecretDisplay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useApiKeyActions } from '@/features/apiKeys/hooks/useApiKeyActions'
import { useRequiredOrgId } from '@/hooks/useRequiredOrgId'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const formSchema = z.object({
  name: z
    .string()
    .min(3, 'API Key name must be at least 3 characters long')
    .max(255, 'API Key name must be less than 255 characters'),
})

export const CreateApiKeyButton = () => {
  const organizationId = useRequiredOrgId()
  const [open, setOpen] = useState(false)
  const [apiKeySecret, setApiKeySecret] = useState<string | null>(null)
  const { createKey } = useApiKeyActions()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  })

  useEffect(() => {
    if (!open) {
      form.reset()
      setApiKeySecret(null)
    }
  }, [open, form])

  function onSubmit(values: z.infer<typeof formSchema>) {
    createKey.mutate(
      { organizationId, name: values.name },
      {
        onSuccess: (data) => {
          if (!data) {
            throw new Error('Failed to create API Key')
          }

          setApiKeySecret(data.secret)
          toast.success('API Key created', {
            description: 'API Key created successfully',
          })
        },
        onError: (error) => {
          toast.error('Error creating API Key', {
            description: error.message,
          })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4" />
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            Create a new API key to authenticate your API requests.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          {apiKeySecret && (
            <OneTimeSecretDisplay
              secret={apiKeySecret}
              label="API Key Secret"
              onDone={() => setOpen(false)}
            />
          )}

          {!apiKeySecret && (
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="pt-2 space-y-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Production key" {...field} />
                    </FormControl>
                    <FormDescription>
                      This is your public display name.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="flex justify-end">
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={createKey.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
